import { useEffect, useMemo, useState } from 'react';

import { SafeComponent } from '@app/web/src/providers/SafeComponent';

import { useProductFeedback } from '@lib/core/products/hooks/useProductFeedback';
import { ProductFeedbackSteps } from '@lib/core/products/slices/productFeedback';
import { useRetailerLocation } from '@lib/core/retailers/hooks/useRetailerLocation';
import { ApiQueries } from '@lib/core/service/consts';
import { useApp } from '@lib/core/service/hooks';
import { BANNER_POSITIONS } from '@lib/tools/shared/helpers/consts';

import { ModalWrapper } from '@components/web/src/templates/Modals/ModalWrapper/ModalWrapper';
import { ProductFeedbackExitAwarenessModal } from '@components/web/src/templates/ProductFeedback/ProductFeedbackExitAwarenessModal/ProductFeedbackExitAwarenessModal';
import { ProductFeedbackPageDone } from '@components/web/src/templates/ProductFeedback/ProductFeedbackPageDone/ProductFeedbackPageDone';
import { ProductFeedbackPageStart } from '@components/web/src/templates/ProductFeedback/ProductFeedbackPageStart/ProductFeedbackPageStart';
import * as S from '@components/web/src/templates/ProductFeedback/ProductFeedbackPageStart/styles';
import { ProductFeedbackQuestionPage } from '@components/web/src/templates/ProductFeedback/ProductFeedbackQuestionPage/ProductFeedbackQuestionPage';
import { ProductFeedbackResultPage } from '@components/web/src/templates/ProductFeedback/ProductFeedbackResultPage/ProductFeedbackResultPage';
import { ProductFeedbackTutorial } from '@components/web/src/templates/ProductFeedback/ProductFeedbackTutorial/ProductFeedbackTutorial';

export const ProductFeedbackModalContainer = () => {
  const [isExitAwarenessModalOpen, setIsExitAwarenessModalOpen] = useState(false);
  const toggleAwarenessModal = () => {
    setIsExitAwarenessModalOpen(!isExitAwarenessModalOpen);
  };

  const {
    getAllSubmittedProductFeedbacks,
    getProductFeedbackQuestions,
    getProductFeedbackBanners,
    isRouteToResetProductFeedbackFlow,
    isProductFeedbackDataOrOptionsLoading,
    isProductFeedbackCurrentQuestionAllAnswersAreLoaded,
    isProductFeedbackAnswersSubmitLoading,
    productFeedbackQuestionsData,
    productFeedbackStep,
    productFeedbackCurrentQuestion,
    parsedProductInstanceData,
    productFeedbackBanners,
    productFeedbackDataLocale,
    productFeedbackTutorialProduct,
    finishedFeedbacksForProduct,
    handleForwardProductFeedbackStep,
    handleSaveProductFeedbackAnswer,
    handleProductFeedbackAnswerDone,
    handleToggleProductFeedbackTutorial,
    handleResetProductFeedbackFlow,
    handleSubmitProductFeedbackAnswers,
    handleToggleOpenedFeedbackModal,
    productCharacterTypeSlug,
  } = useProductFeedback();

  const { locale } = useApp();
  const { retailerLocationName } = useRetailerLocation();

  useEffect(() => {
    if (!parsedProductInstanceData?.productId || !productCharacterTypeSlug) {
      return;
    }

    getProductFeedbackBanners({
      [ApiQueries.positionsIdentifier]: BANNER_POSITIONS.PRODUCT_FEEDBACK,
      [ApiQueries.characterTypeSlug]: productCharacterTypeSlug,
    });

    const shouldFetchQuestions =
      !parsedProductInstanceData.productFeedbackStatus?.isFinished &&
      (productFeedbackStep === ProductFeedbackSteps.Questions || productFeedbackStep === ProductFeedbackSteps.Start);

    if (shouldFetchQuestions) {
      getProductFeedbackQuestions();
    }

    if (parsedProductInstanceData.productFeedbackStatus?.isFinished && productFeedbackDataLocale !== locale) {
      getAllSubmittedProductFeedbacks(true);
    }
    // Intentionally limited to locale/product identity: refetch on language change only;
    // step transitions and stable callbacks are excluded to avoid duplicate requests.
  }, [locale, parsedProductInstanceData?.productId, productCharacterTypeSlug]);

  useEffect(() => {
    if (isRouteToResetProductFeedbackFlow) {
      handleResetProductFeedbackFlow();
    }
  }, [isRouteToResetProductFeedbackFlow]);

  let Component;

  const StartComponent = useMemo(
    () => (
      <ProductFeedbackPageStart
        handleNavigate={handleForwardProductFeedbackStep}
        isLoading={isProductFeedbackDataOrOptionsLoading}
        parsedProductInstanceData={parsedProductInstanceData}
      />
    ),
    [isProductFeedbackDataOrOptionsLoading],
  );

  switch (productFeedbackStep) {
    case ProductFeedbackSteps.Start:
      Component = StartComponent; // Memoize because re-renders of FeedbackModalContainerx resets a timer
      break;
    case ProductFeedbackSteps.Questions:
    case ProductFeedbackSteps.Comment:
    case ProductFeedbackSteps.Rating:
      if (productFeedbackQuestionsData) {
        Component = (
          <ProductFeedbackQuestionPage
            banners={productFeedbackBanners}
            handleClose={handleToggleOpenedFeedbackModal}
            handleProductFeedbackAnswerDone={handleProductFeedbackAnswerDone}
            handleSkip={handleForwardProductFeedbackStep}
            handleSubmitAnswer={handleSaveProductFeedbackAnswer}
            handleSubmitProductFeedbackAnswers={handleSubmitProductFeedbackAnswers}
            isLoading={!isProductFeedbackCurrentQuestionAllAnswersAreLoaded || isProductFeedbackDataOrOptionsLoading}
            isProductFeedbackAnswersSubmitLoading={isProductFeedbackAnswersSubmitLoading}
            parsedProductInstanceData={parsedProductInstanceData}
            productFeedbackCurrentQuestion={productFeedbackCurrentQuestion}
            productFeedbackStep={productFeedbackStep}
            questionsAmount={productFeedbackQuestionsData.choices?.questions?.length}
            subtitle={retailerLocationName}
          />
        );
      }
      break;
    case ProductFeedbackSteps.Done:
      Component = (
        <ProductFeedbackPageDone
          handleNavigate={handleToggleOpenedFeedbackModal}
          locale={locale}
          parsedProductInstanceData={parsedProductInstanceData}
        />
      );
      break;
    case ProductFeedbackSteps.ProductFeedbackResult:
      Component = (
        <ProductFeedbackResultPage
          answers={finishedFeedbacksForProduct}
          handleGoBack={handleToggleOpenedFeedbackModal}
          parsedProductInstanceData={parsedProductInstanceData}
          retailerLocationName={retailerLocationName}
        />
      );
      break;
    case ProductFeedbackSteps.Tutorial:
    default:
      Component = (
        <ProductFeedbackTutorial
          handleClose={() => handleToggleProductFeedbackTutorial({})}
          parsedProductInstanceData={productFeedbackTutorialProduct}
        />
      );
      break;
  }

  const isTutorialProductFeedbackStep = productFeedbackStep === ProductFeedbackSteps.Tutorial;

  return (
    <SafeComponent onError={handleResetProductFeedbackFlow}>
      <ModalWrapper
        isOpen
        isTransparent={isTutorialProductFeedbackStep}
        minModalContentHeight={!isTutorialProductFeedbackStep && '100%'}
        withoutDefaultPadding={!isTutorialProductFeedbackStep}
      >
        {!isTutorialProductFeedbackStep && (
          <S.StyledBackButton
            isForModal
            handleClick={
              productFeedbackStep === ProductFeedbackSteps.Questions ||
              productFeedbackStep === ProductFeedbackSteps.Rating ||
              productFeedbackStep === ProductFeedbackSteps.Comment
                ? toggleAwarenessModal
                : handleToggleOpenedFeedbackModal
            }
          />
        )}
        {Component}
        {isExitAwarenessModalOpen && (
          <ProductFeedbackExitAwarenessModal
            isOpen
            handleCancelTasteClick={handleToggleOpenedFeedbackModal}
            handleContinueTasteClick={toggleAwarenessModal}
            hideModal={toggleAwarenessModal}
          />
        )}
      </ModalWrapper>
    </SafeComponent>
  );
};
